import { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import { getCommunityLogoUrl } from 'src/utils/linkUtils';
import i18n from 'src/utils/translate';
import { text, textSmall } from 'styles-js/mixins/typography';
import { followCommunity, unfollowCommunity } from 'src/modules/community/shared/actions';
import Tooltip from 'src/components/tooltip';
import EventType from 'src/constants/footfallEventTypes';
import { trackFootfallEvent } from 'src/utils/footfall';

const FindCommunityCard = ({
  community,
  following,
  hidden,
}) => {
  const [disabled, setDisabled] = useState(false);
  // @ts-expect-error ts-migrate(2339) FIXME: Property 'context' does not exist on type 'Default... Remove this comment to see the full error message
  const currentUserId = useSelector(state => !!state.context.currentUser && state.context.currentUser.userId);
  const dispatch = useDispatch();

  const {
    name,
    title,
    slug,
    followerCount,
    logoImageHash,
    imageUrl,
    url,
  } = community;

  const communitySlug = url ? url.replace('https://healthunlocked.com/', '') : slug;

  const onClick = () => {
    if (disabled) return;
    setDisabled(true);
    const formattedCommunity = {...community, slug: communitySlug, name: name || title};
    if (following) {
      dispatch(unfollowCommunity(formattedCommunity, currentUserId, null, true));
    } else {
      dispatch(followCommunity(formattedCommunity, currentUserId, null));
    }
    setDisabled(false);
  };

  const clickOnInfoIcon = () => {
    trackFootfallEvent(EventType.CLICKED, {
      clickType: 'info-icon-read-only-community', clickMetadata: { section: 'communities-join' },
    });
  };

  if (hidden) return null;

  return (
    <Container>
      <Content>
        <Logo
          imageUrl={imageUrl}
          logoImageHash={logoImageHash}
        />
        <TextWrapper>
          <Name className="community-title">{name || title}</Name>
          <Description>
            {!!followerCount && <Followers>{followerCount.toLocaleString()}{' '}{i18n.t('members')}</Followers>}
            {community.slug === 'blog' &&
              <Tooltip
                text={i18n.t('This is a read-only community to share HealthUnlocked news.')}
                onClickOrHover={clickOnInfoIcon}
              />
            }
          </Description>
        </TextWrapper>
      </Content>
      <IconWrapper className="icon-wrapper" onClick={onClick}>
        {following
          ? <Icon className="icon icon-cross-2" />
          : <Icon className="icon icon-plus-2" />
        }
      </IconWrapper>
    </Container>
  );
};

FindCommunityCard.propTypes = {
  community: PropTypes.object.isRequired,
  following: PropTypes.bool,
  hidden: PropTypes.bool,
};

FindCommunityCard.defaultProps = {
  following: false,
  hidden: false,
};

const Container = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  background: none;
  border: none;
  padding: 0;
`;

const Content = styled.div`
  flex-grow: 1;
  display: flex;
  margin-bottom: 5px;
  align-items: center;
`;

const Logo = styled.div<{imageUrl: string, logoImageHash: string}>`
  background-image: url("${({ imageUrl, logoImageHash }) => imageUrl ? imageUrl : getCommunityLogoUrl(logoImageHash)}");
  border-radius: 50%;
  height: 40px;
  width: 40px;
  background-clip: padding-box;
  background-size: cover;
  margin-right: 10px;
  flex-shrink: 0;
`;

const TextWrapper = styled.div`
  margin: auto 0;
`;

const Name = styled.div`
  ${text};
  text-align: left;
  padding-right: 4px;
`;

const Followers = styled.div`
  ${textSmall};
  color: ${props => props.theme.colorTextLight};
  text-align: left;
`;

const Description = styled.div`
  display: flex;
`;

const IconWrapper = styled.div`
  width: 40px;
  height: 40px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

const Icon = styled.span`
  margin-right: 4px;

  &::before {
    color: ${props => props.theme.colorTextLinkPrimary};
    font-weight: bold;
    font-size: 20px;
  }
`;

export default FindCommunityCard;
